import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarDays } from 'lucide-react';

export default function AcademicCalendar({ 
  accent = "#115e32",
  pageTitle = "Academic Calendar | University of Creative Technology Chittagong",
}) {
  const [calendar, setCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const acPost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,				
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/academic-calendar", payload);
			
			
			// Loop data (array)
			setCalendar(response.data || []);			
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {		
		acPost();
	}, []);
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#6BCCF2] to-teal-50 text-[#014864]">
      {/* Hero Section */}
      <section className="relative overflow-hidden text-center py-24">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10"
        >
          <div className="flex justify-center mb-6">
            <CalendarDays className="text-[#015A7A] w-16 h-16" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#014864] mb-4">
            Academic Calendar
          </h1>
          <p className="text-[#015A7A] text-lg max-w-2xl mx-auto">
            Track semester schedules, sessions, and events with our dynamic timeline view.
          </p>
        </motion.div>

        {/* Animated SVG background */}
        <svg
          className="absolute bottom-0 left-0 w-full h-24 text-yellow-200"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
        >
          <path
            fill="#014864"
            d="M0,224L48,192C96,160,192,96,288,90.7C384,85,480,139,576,176C672,213,768,235,864,218.7C960,203,1056,149,1152,138.7C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>
      </section>

      {/* Timeline Section */}
      <section className="relative max-w-5xl mx-auto px-6 py-16">
        {loading ? (
          <div className="text-center text-[#014864] font-medium">
            Loading academic calendar...
          </div>
        ) : (
          <div className="relative">
            {/* Vertical Timeline Line */}
            <div className="absolute left-1/2 top-0 h-full w-1 bg-yellow-400 transform -translate-x-1/2"></div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="space-y-16"
            >
              {calendar.map((item, index) => (
                <motion.div
                  key={item.SL}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className={`flex flex-col md:flex-row items-center ${
                    index % 2 === 0 ? 'md:justify-start' : 'md:justify-end'
                  } relative`}
                >
                  {/* Connector Dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 bg-[#014864] w-6 h-6 rounded-full border-4 border-amber-100 z-10"></div>

                  {/* Content Card */}
                  <div
                    className={`bg-white rounded-2xl shadow-lg border border-yellow-200 p-6 w-full md:w-[45%] ${
                      index % 2 === 0 ? 'md:mr-auto md:text-right' : 'md:ml-auto md:text-left'
                    }`}
                  >
                    <h3 className="text-2xl font-bold text-[#014864] mb-1">
                      {item.name}
                    </h3>
                    <p className="text-[#015A7A] font-medium mb-1">
                      Semester: {item.semester}
                    </p>
                    <p className="text-[#015A7A]">
                      <strong>Start:</strong> {item.start_date}
                    </p>
                    <p className="text-[#015A7A]">
                      <strong>End:</strong> {item.end_date}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        )}
      </section>
    </div>
  );
}